<script setup lang="ts">
import { computed, inject, ref, toRaw, watch } from "vue";
import {
  ActionButtonType,
  FormElementType,
  SimpleTextFormElement,
  SpecialQuestion,
} from "~/api";
import {
  type WorkflowState,
  workflowInjectKey,
} from "~/Compliance/composables/useWorkflow";
import { validationDict } from "~/NewFormBuilder/utils/validationTypeOptions";
import { usePdfLoadingStore } from "~/stores/pdfLoadingStore";
import { apiClient } from "~/utils/apiClient";
import { showError } from "~/utils/handleAPIError";
import { hubManager } from "~/utils/hubManager";
import {
  FormResponseState,
  formResponseInjectKey,
} from "../../composables/useFormResponse";
import { DefaultFormComponentProps } from "./FormComponentRenderer.vue";

const props = defineProps<DefaultFormComponentProps<SimpleTextFormElement>>();

const { useAnswer, substituteTemplatedText, updateWorkflowName } = inject(
  formResponseInjectKey,
  // Cast the type since we know it is always provided. For more info see:
  // https://vuejs.org/guide/typescript/composition-api.html#typing-provide-inject
) as FormResponseState;
const answer = useAnswer<FormElementType.SIMPLE_TEXT>(props.modelValue);
const elementProperties = computed(() => props.modelValue.properties);

// If no answer has been provided yet, use the default answer from properties
if (answer.value === undefined && elementProperties.value.defaultAnswer) {
  answer.value = elementProperties.value.defaultAnswer;
}

const localAnswer = ref<string | undefined>(
  structuredClone(toRaw(answer.value)),
);

const { getWorkflow } = inject(workflowInjectKey, {
  updateWorkflowStatus: async (status: string) => {
    console.log("No workflow in designer. Status to update to:", status);
  },
  updateWorkflowStatusFromNameStamp: async (status: string) => {
    console.log("No workflow in designer. Status to update to:", status);
  },
  getWorkflowStatus: () => {
    return "";
  },
  getWorkflow: () => {
    return undefined;
  },
}) as WorkflowState;

const pdfLoadingStore = usePdfLoadingStore();

const workflowGuid = computed(
  () => getWorkflow()?.guid || pdfLoadingStore.workflowGuid || undefined,
);

const makePdf = async (pdfCode?: string) => {
  if (!workflowGuid.value) {
    showError("Workflow instance not found");
    return;
  }
  // Ensure we pass the ConnectionId for the "self" hub (where PdfMsg listener is).
  await hubManager.getHub("self", "/self");
  const selfConnId =
    hubManager.getHubConnectionId("self") ?? hubManager.ConnectionId ?? "";
  await pdfLoadingStore.makePdf(pdfCode, workflowGuid.value, selfConnId);
};

const sendEmail = async () => {
  if (!workflowGuid.value) {
    showError("Workflow instance not found");
    return;
  }
  const eventCode = elementProperties.value.code;
  if (!eventCode) {
    showError("Event code is missing.");
    return;
  }
  await apiClient.workflowInstance.handleSendEmailEvent(
    workflowGuid.value,
    eventCode,
  );
};

const runPeopleMigration = async () => {
  if (!workflowGuid.value) {
    showError("Workflow instance not found");
    return;
  }
  try {
    const result = await apiClient.workflowInstance.handleRunPeopleMigration(
      workflowGuid.value,
    );
    console.log(
      `People migration complete: ${result.peopleCreated} created, ${result.skipped} skipped`,
    );
  } catch (e) {
    console.error("People migration failed.", e);
  }
};

const executeAction = async () => {
  const action = elementProperties.value.actionToTake;
  if (!action || action === ActionButtonType.NONE) {
    return;
  }

  if (action === ActionButtonType.GET_PDF) {
    if (elementProperties.value.code) {
      await makePdf(elementProperties.value.code);
    } else {
      await makePdf();
    }
  } else if (action === ActionButtonType.SEND_EMAIL) {
    await sendEmail();
  } else if (action === ActionButtonType.RUN_PEOPLE_MIGRATION) {
    await runPeopleMigration();
  } else {
    showError("Unsupported action type:" + action);
  }
};

const getIsValidInput = (input?: string): boolean => {
  // Count these as valid to allow deleting answers
  if (input === undefined || input === null || input === "") {
    return true;
  }

  const validator =
    validationDict[elementProperties.value.validationType!]?.validator;

  if (validator === undefined) {
    return true;
  }

  if (wordCountStatus.value === "error") {
    return false;
  }

  return validator(input);
};

// Track whether the answer change was user-initiated (vs server-computed)
const isUserUpdate = ref(false);

const handleUpdateAnswer = async (newVal: string) => {
  // Always save the answer to allow form-level validation to catch errors
  // This ensures questions with validation errors are marked as "invalid" not "unanswered"
  isUserUpdate.value = true;
  answer.value = newVal;

  // Check if this is a SpecialQuestion that should update the workflow name
  if (
    elementProperties.value.specialQuestion ===
    SpecialQuestion.UPDATE_WORKFLOW_NAME
  ) {
    // Update the workflow name in memory to prevent stale data issues
    updateWorkflowName(newVal);
  }
};

const countWords = (text: string) => {
  if (!text || text.trim() === "") {
    return 0;
  }
  const words = text.match(/\b\w+([-'\u2018\u2019]\w+)*\b/g);
  return words ? words.length : 0;
};

const currentWordCount = computed(() => countWords(localAnswer.value ?? ""));

const wordCountDisplay = computed(() => {
  if (!elementProperties.value.maxWordCount) {
    return null;
  }

  const current = currentWordCount.value;
  const isEmpty = !localAnswer.value || localAnswer.value.trim() === ""; // if no words entered yet

  if (isEmpty) {
    return `Maximum ${elementProperties.value.maxWordCount} words`;
  } else {
    return `Maximum ${elementProperties.value.maxWordCount} words - ${current} entered`;
  }
});

const wordCountStatus = computed(() => {
  const maxWords = elementProperties.value.maxWordCount;
  if (!maxWords) {
    return "valid";
  }

  const current = currentWordCount.value;
  const warningThreshold = Math.ceil(maxWords * 0.9); // 10% words to go, rounded up

  if (current > maxWords) {
    return "error";
  }
  if (current >= warningThreshold) {
    return "warning";
  }
  return "valid";
});

watch(
  () => answer.value,
  async () => {
    localAnswer.value = answer.value;
    const shouldExecuteAction = isUserUpdate.value;
    isUserUpdate.value = false;
    if (shouldExecuteAction) {
      await executeAction();
    }
  },
);
</script>

<template>
  <div
    class="SimpleTextComponent"
    :class="{
      SimpleTextComponent__multiline: modelValue.properties.isMultiline,
    }"
  >
    <slot name="label" :element="modelValue" />

    <div style="flex-grow: 1"></div>
    <span
      v-if="elementProperties.autocomplete !== 'off'"
      class="autocomplete-attribute"
    >
      {{ elementProperties.autocomplete }}
    </span>
    <span v-if="!getIsValidInput(localAnswer)" class="invalid-input">
      Invalid Input
    </span>
    <el-input
      v-model="localAnswer"
      :type="modelValue.properties.isMultiline ? 'textarea' : 'text'"
      :autosize="modelValue.properties.isMultiline ? { minRows: 2 } : false"
      :maxlength="modelValue.properties.maxLength ?? undefined"
      :show-word-limit="(modelValue.properties.maxWordCount ?? 0) > 10"
      :placeholder="
        substituteTemplatedText(modelValue.properties.placeholder) ?? ''
      "
      :autocomplete="elementProperties.autocomplete ?? 'off'"
      :disabled="isReadOnly"
      :formatter="
        validationDict[modelValue.properties?.validationType!]?.formatter
      "
      :parser="validationDict[modelValue.properties?.validationType!]?.parser"
      @change="handleUpdateAnswer"
    ></el-input>
  </div>
  <div
    v-if="wordCountDisplay"
    class="word-count-display"
    :class="{
      'word-count-display-warning': wordCountStatus === 'warning',
      'word-count-display-error': wordCountStatus === 'error',
    }"
  >
    {{ wordCountDisplay }}
  </div>
</template>

<style lang="less">
.SimpleTextComponent {
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 10px;

  .simple-text-label {
    font-size: 1em;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .simple-text-optional {
    font-size: 1em;
    font-style: italic;
    display: inline;
  }

  .autocomplete-attribute,
  .simple-text-optional {
    color: #999;
  }

  .invalid-input,
  .autocomplete-attribute {
    font-size: 0.9em;
  }
  .invalid-input {
    color: var(--el-color-error);
  }

  .el-textarea__inner {
    box-shadow: 0 0 0 2px var(--el-input-border-color, var(--el-border-color));
    max-height: 75vh;
  }
  .el-input.is-disabled {
    .el-input__wrapper {
      background-color: var(--el-color-info-light-9);
    }
    .el-input__inner {
      -webkit-text-fill-color: #444142;
    }
  }
}

.SimpleTextComponent__multiline {
  gap: 0;
  flex-direction: column;
  padding: 3px 0;
  .el-textarea.is-disabled .el-textarea__inner {
    -webkit-text-fill-color: #444142;
    background-color: var(--el-color-info-light-9);
  }
}

.word-count-display {
  font-size: 0.75em;
  color: #666;
  margin-top: 2px;
  margin-left: auto;
  align-self: flex-end;

  &-warning {
    color: #e6a23c;
  }

  &-error {
    color: var(--el-color-error);
  }
}
</style>
